<template>
  <section>
    <LayoutAccount>
      <template #title>
        {{ $t('pages.login.headline_title_line1') }} {{ $t('pages.login.headline_title_line2') }}
      </template>

      <template #mainContent>
        <div class="login-providers" :class="socialPositionClass">

          <!-- Main Forms -->
          <div class="main-form">
            <!-- JWT Auto-Login (highest priority - detected from ?jwt=xxx) -->
            <div v-if="isProviderEnabled('jwt')" class="provider-section">
              <JwtAuthForm @success="handleLoginSuccess" @error="handleAuthError" />
            </div>
            <!-- SSO Auto-Login (detected from ?t=xxx&k=yyy) -->
            <div v-else-if="isProviderEnabled('sso')" class="provider-section">
              <SsoAuthForm @success="handleLoginSuccess" @error="handleAuthError" />
            </div>
            <!-- Magic Link Auto-Verify (detected from ?magic_token=xxx&email=yyy) -->
            <div v-else-if="hasMagicLinkParams" class="provider-section">
              <MagicLinkAuthForm @success="handleLoginSuccess" @error="handleAuthError" />
            </div>
            <!-- Standard login forms (only when NOT jwt/sso/magic_link auto-verify) -->
            <template v-else>
              <!-- Magic Link Passwordless (highest priority among manual methods) -->
              <div v-if="isProviderEnabled('magic_link')" class="provider-section">
                <MagicLinkForm />
              </div>

              <!-- Email Token Passwordless (priority) -->
              <div v-else-if="isProviderEnabled('email_token')" class="provider-section">
                <EmailTokenUnifiedForm @success="handleLoginSuccess" />
              </div>

              <!-- OR Divider (se ci sono altri provider dopo magic_link/email_token) -->
              <div v-if="(isProviderEnabled('magic_link') || isProviderEnabled('email_token')) && hasOtherProviders" class="divider-or">
                <hr>
              </div>

              <!-- Password Login/Signup -->
              <div v-if="isProviderEnabled('password')" class="provider-section">
                <div v-if="showPasswordSignup">
                  <PasswordSignupForm @success="handleLoginSuccess" />
                </div>
                <div v-else>
                  <PasswordLoginForm @success="handleLoginSuccess" />
                </div>
              </div>
            </template>
          </div>

          <!-- Social Login Buttons (hidden when auto-login: JWT/SSO/MagicLink) -->
          <div v-if="!isProviderEnabled('jwt') && !isProviderEnabled('sso') && !hasMagicLinkParams" class="social-buttons">
            <SocialAuthButtons
              :apple-disabled="appleDisabled"
              :title-key="socialPosition === 'top' ? 'forms.login_form.social_login_title_top' : undefined"
              @success="handleLoginSuccess"
              @error="handleSocialError"
            />
          </div>

          <!-- Powered By (per specifici subdomain) -->
          <div class="row d-lg-flex align-items-center mt-4" v-if="showPoweredBy">
            <div class="col-lg-12">
              <PoweredBy :url="poweredByUrl" />
            </div>
          </div>
        </div>
      </template>
    </LayoutAccount>

    <B2BSection v-if="isPicaThirdLevelDomain" />

  </section>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import LayoutAccount from '@/components/LayoutAccount'
import B2BSection from '@/components/B2BSection.vue'
import PoweredBy from '@/components/PoweredBy.vue'

// Provider components
import EmailTokenUnifiedForm from '@/domain/auth/components/providers/EmailTokenUnifiedForm.vue'
import MagicLinkForm from '@/domain/auth/components/providers/MagicLinkForm.vue'
import MagicLinkAuthForm from '@/domain/auth/components/providers/MagicLinkAuthForm.vue'
import PasswordLoginForm from '@/domain/auth/components/providers/PasswordLoginForm.vue'
import PasswordSignupForm from '@/domain/auth/components/providers/PasswordSignupForm.vue'
import SocialAuthButtons from '@/domain/auth/components/SocialAuthButtons.vue'
import JwtAuthForm from '@/domain/auth/components/providers/JwtAuthForm.vue'
import SsoAuthForm from '@/domain/auth/components/providers/SsoAuthForm.vue'

export default {
  name: 'Login',

  components: {
    LayoutAccount,
    B2BSection,
    PoweredBy,
    EmailTokenUnifiedForm,
    MagicLinkForm,
    MagicLinkAuthForm,
    PasswordLoginForm,
    PasswordSignupForm,
    SocialAuthButtons,
    JwtAuthForm,
    SsoAuthForm,
  },

  data() {
    return {
      showPasswordSignup: false,
      socialError: false
    }
  },

  computed: {
    ...mapGetters(['isPicaThirdLevelDomain', 'thirdLevelDomain', 'disableSocialLoginInPreview']),
    ...mapGetters('auth', [
      'enabledAuthProviders',
      'isAuthProviderEnabled',
      'enabledAuthProvidersWithQuery',
      'hasSocialLoginEnabled',
      'hasAcceptedRequiredPolicies'
    ]),

    /**
     * Get list of enabled providers considering query params
     * This allows JWT/SSO auto-detection from URL
     */
    currentEnabledProviders() {
      const providers = this.enabledAuthProvidersWithQuery(this.$route.query)
      console.log('[Login.vue] currentEnabledProviders:', providers)
      console.log('[Login.vue] Query params:', this.$route.query)
      return providers
    },

    /**
     * Check if magic link params are in URL for auto-verification
     */
    hasMagicLinkParams() {
      return !!(this.$route.query.magic_link && this.$route.query.email)
    },

    appleDisabled() {
      return this.disableSocialLoginInPreview
    },

    hasSocialProviders() {
      return this.isProviderEnabled('google') ||
             this.isProviderEnabled('facebook') ||
             (this.isProviderEnabled('apple') && !this.appleDisabled)
    },

    hasOtherProviders() {
      return this.isProviderEnabled('password') || this.hasSocialProviders
    },

    showPoweredBy() {
      const domains = ['hublot']
      return domains.includes(this.thirdLevelDomain)
    },

    poweredByUrl() {
      return `https://business.getpica.com/?utm_source=${this.thirdLevelDomain}.getpica.com&utm_medium=website&utm_campaign=${this.thirdLevelDomain}`
    },

    socialPosition() {
      if (['photos'].includes(this.thirdLevelDomain) ) return 'top'
      return 'bottom'
    },

    socialPositionClass() {
      return `social-position-${this.socialPosition}`
    },

    appleDisabled() {
      return this.disableSocialLoginInPreview
    },
  },

  methods: {
    ...mapActions('auth', ['getCurrentUser']),

    /**
     * Check if provider is enabled with query params awareness
     */
    isProviderEnabled(provider) {
      const isEnabled = this.currentEnabledProviders.includes(provider)
      console.log(`[Login.vue] isProviderEnabled('${provider}'):`, isEnabled)
      return isEnabled
    },

    togglePasswordMode() {
      this.showPasswordSignup = !this.showPasswordSignup
    },

    async handleLoginSuccess() {
      // Get fresh user data
      await this.getCurrentUser()

      // Check if user has accepted required policies
      if (!this.hasAcceptedRequiredPolicies) {
        await this.$router.push({ name: 'terms', params: { next: 'checkins-list' } })
      } else {
        await this.$router.push({ name: 'checkins-list' })
      }
    },

    handleSocialError() {
      this.socialError = true
      setTimeout(() => {
        this.socialError = false
      }, 5000)
    },

    handleAuthError(error) {
      console.error('Authentication error:', error)
      // On JWT/SSO auth failure, URL will likely be cleaned and user will see standard forms
      // We could also redirect to homepage or show error message
    }
  }
}
</script>

